import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { CalendarNavigationConfig, CalendarGrid, CalendarEvent } from '@frontend/calendar-grid';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { Button, ContentLoader, Heading } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../constants';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AvailabilityHeader } from './week-view-availability-header/availability-header';
import { TimeRange } from './week-view-availability-header/availability-header.types';
import { useAvailabilityFiltersWithBookableData } from './week-view-availability-header/use-availability-filters-with-bookable-data';

const Loader = () => <ContentLoader show />;
const NoAvailability = ({ onJumpToNextWeek }: { onJumpToNextWeek: () => void }) => {
  const { t } = useTranslation('schedule');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 'var(--fabric-space-gap-lg)',
      }}
    >
      <Heading level={3} css={{ marginBottom: 'var(--fabric-space-gap-lg)' }}>
        {t('No slots available')}
      </Heading>
      <Button variant='primary' onClick={onJumpToNextWeek} iconName='calendar'>
        {t('Jump to next week')}
      </Button>
    </div>
  );
};

const getTimeRangeForSlot = (slotHour: number, timeRanges: TimeRange[]) =>
  timeRanges.find(({ startHour, endHour }) => slotHour >= startHour && slotHour < endHour);

const getSlotKey = (providerId: string | undefined, date: string, startDateTime: string, slotIndex: number) =>
  providerId ? `${providerId}-${date}-${startDateTime}-${slotIndex}` : `${date}-${startDateTime}-${slotIndex}`;

const convertBookableSlotsIntoDateWiseEvents = (
  providerSlots?: { providerId?: string; slots?: { startDateTime?: string; endDateTime?: string }[] }[],
  selectedTimeRanges?: TimeRange[],
  locationTimeZone?: string
): Record<string, { id: string; start: string; label: string }[]> => {
  if (!providerSlots) return {};

  return providerSlots.reduce<Record<string, { id: string; start: string; label: string }[]>>((acc, providerSlot) => {
    providerSlot.slots?.forEach((slot, slotIndex) => {
      const startDateTime = slot.startDateTime;
      if (!startDateTime) {
        return;
      }

      const slotDateTime = locationTimeZone ? dayjs.tz(startDateTime, locationTimeZone) : dayjs(startDateTime);
      const date = slotDateTime.format(DATETIME_FORMATS.ISO_DATE);

      const matchedTimeRange = getTimeRangeForSlot(slotDateTime.hour(), selectedTimeRanges ?? []);
      if (!matchedTimeRange) {
        return;
      }

      acc[date] ??= [];
      acc[date].push({
        id: getSlotKey(providerSlot.providerId, date, startDateTime, slotIndex),
        start: startDateTime,
        label: matchedTimeRange.label,
      });
    });

    return acc;
  }, {});
};

const getWeekBoundary = (date: string, locationTimeZone: string, boundary: 'start' | 'end') => {
  const base = locationTimeZone ? dayjs.tz(date, locationTimeZone) : dayjs(date);
  return boundary === 'start'
    ? base.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET)
    : base.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
};

const getSlotQueryDateBoundary = (
  weekStartDate: string,
  weekEndDate: string,
  locationTimeZone: string
): { startDateTime: string; endDateTime: string } => {
  const today = locationTimeZone ? dayjs().tz(locationTimeZone) : dayjs();
  const todayDate = today.format(DATETIME_FORMATS.ISO_DATE);

  // Use the maximum of today and the week start date to ensure we never query in the past
  const effectiveStartDate = dayjs(weekStartDate).isAfter(dayjs(todayDate)) ? weekStartDate : todayDate;

  return {
    startDateTime: getWeekBoundary(effectiveStartDate, locationTimeZone, 'start'),
    endDateTime: getWeekBoundary(weekEndDate, locationTimeZone, 'end'),
  };
};

const convertApiSlotTime = (time: string, locationTimeZone?: string, format: string = DATETIME_FORMATS.US_TIME) =>
  (locationTimeZone ? dayjs.tz(time, locationTimeZone) : dayjs(time)).format(format);

export const ScheduleAvailabilityPageBookableSlots = ({ appointmentTypeId }: { appointmentTypeId?: string }) => {
  const { t } = useTranslation('schedule');

  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const initialLocationId = selectedLocationIds[0];

  const { resolvedFilters, headerFieldProps, bookableAppointmentTypes } = useAvailabilityFiltersWithBookableData({
    locationId: initialLocationId,
    orgId: selectedOrgId,
    initialServiceId: appointmentTypeId,
  });
  const locationId = resolvedFilters.locationId;
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const selectedLocationTimeZone = getLocationTimeZone(locationId ?? '');

  const selectedBookableAppointmentType = bookableAppointmentTypes?.find(
    (appointmentType) => appointmentType.id === resolvedFilters.serviceId
  );

  const slotDuration = selectedBookableAppointmentType?.durationMinutes ?? 10;

  const { startDateTime, endDateTime } = getSlotQueryDateBoundary(
    resolvedFilters.weekStartDate,
    resolvedFilters.weekEndDate,
    selectedLocationTimeZone
  );

  const bookableSlotsQuery = SchedulerV3.Queries.useListBookableAppointmentSlotsQuery({
    request: {
      startDateTime,
      endDateTime,
      locationId,
      providerIds: resolvedFilters.providerIds,
      bookableAppointmentTypeId: resolvedFilters.serviceId,
      slotDuration,
      requestSource: 'BOOKING_SITE',
    },
    options: {
      enabled: !!locationId && !!selectedOrgId && !!resolvedFilters.serviceId && resolvedFilters.providerIds.length > 0,
    },
  });

  const { columns, events } = useMemo(() => {
    const filteredSlotsByDate = convertBookableSlotsIntoDateWiseEvents(
      bookableSlotsQuery.data?.providerSlots,
      resolvedFilters.timeRanges,
      selectedLocationTimeZone
    );

    const calendarEvents: CalendarEvent[] = [];
    const eventIdsByDate: Record<string, string[]> = {};

    Object.entries(filteredSlotsByDate).forEach(([date, slots]) => {
      const slotsByTimeRange = slots.reduce<Record<string, typeof slots>>((acc, slot) => {
        acc[slot.label] ??= [];
        acc[slot.label].push(slot);
        return acc;
      }, {});

      resolvedFilters.timeRanges.forEach(({ label, startHour: timeRangeStartHour }) => {
        const slotsInRange = (slotsByTimeRange[label] ?? []).sort((left, right) =>
          left.start.localeCompare(right.start)
        );

        if (slotsInRange.length === 0) {
          return;
        }

        const labelEventId = `${date}-${label.replace(/\s+/g, '-')}-label`;

        eventIdsByDate[date] ??= [];
        eventIdsByDate[date].push(labelEventId);

        calendarEvents.push({
          id: labelEventId,
          eventType: 'slotsStartLabel',
          startTime: dayjs().hour(timeRangeStartHour).minute(0).format(DATETIME_FORMATS.US_TIME),
          duration: 0,
          label,
        });

        slotsInRange.forEach((slot) => {
          const eventId = `${date}-${slot.id}`;
          eventIdsByDate[date].push(eventId);
          calendarEvents.push({
            id: eventId,
            eventType: 'nonInteractiveSlot',
            startTime: convertApiSlotTime(slot.start, selectedLocationTimeZone),
            duration: 0,
          });
        });
      });
    });

    return {
      columns: Array.from({ length: 7 }, (_, index) => {
        const date = dayjs(resolvedFilters.weekStartDate, DATETIME_FORMATS.ISO_DATE).add(index, 'day');
        const dateKey = date.format(DATETIME_FORMATS.ISO_DATE);

        return {
          id: `availability-column-${dateKey}`,
          name: date.format('dddd'),
          dayOfMonth: date.date(),
          slotsCount: filteredSlotsByDate[dateKey]?.length ?? 0,
          eventIds: eventIdsByDate[dateKey] ?? [],
          groupId: locationId,
        };
      }),
      events: calendarEvents,
    };
  }, [
    bookableSlotsQuery.data?.providerSlots,
    resolvedFilters.timeRanges,
    resolvedFilters.weekStartDate,
    selectedLocationTimeZone,
    locationId,
  ]);

  const navigationConfig = useMemo<CalendarNavigationConfig>(() => {
    return {
      canGoNext: true,
      canGoPrevious: !headerFieldProps.isCurrentWeek,
      onNext: headerFieldProps.goToNextWeek,
      onPrevious: headerFieldProps.goToPreviousWeek,
    };
  }, [headerFieldProps.goToNextWeek, headerFieldProps.goToPreviousWeek, headerFieldProps.isCurrentWeek]);

  const renderEmptyStateComponent = useCallback(
    (_groupIds: string[], variant: 'no-events' | 'no-columns') => {
      if (bookableSlotsQuery.isLoading) return <Loader />;

      if (variant === 'no-events') {
        return <NoAvailability onJumpToNextWeek={headerFieldProps.goToNextWeek} />;
      }
      return null;
    },
    [headerFieldProps.goToNextWeek, bookableSlotsQuery.isLoading]
  );

  return (
    <section css={styles.container}>
      <Heading level={3} css={{ margin: 'var(--fabric-space-gap-md)' }}>
        {t('Availability Finder')}
      </Heading>
      <AvailabilityHeader locationId={locationId} orgId={selectedOrgId} {...headerFieldProps} />
      <section css={styles.gridSection}>
        <CalendarGrid
          columnBodyVariant='stacked'
          headerType='weekday'
          events={bookableSlotsQuery.isLoading ? [] : events}
          columns={columns}
          renderEmptyStateComponent={renderEmptyStateComponent}
          navigationConfig={navigationConfig}
        />
      </section>
    </section>
  );
};

const styles = {
  container: css({
    height: '100%',
    minHeight: 0,
    borderRadius: 'var(--border-radius-small)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }),
  gridSection: css({
    flex: 1,
    minHeight: 0,
  }),
};
